import React, { type CSSProperties } from 'react'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import 'react-lazy-load-image-component/src/effects/blur.css'
import {
  ACTION_PLATE_BUTTON_BG,
  CANCEL_BUTTON_BG,
  IMMEDIATE_BUTTON_BG,
} from '../utils/startClaimButtonStyle'

interface GameButtonProps {
  children: React.ReactNode
  onClick?: (e?: React.MouseEvent<HTMLButtonElement>) => void
  disabled?: boolean
  className?: string
  size?: 'default' | 'small'
  /** plate = Start/Claim (`button.png`); cancel = `cancel-btn.png`; immediate = `immediate-btn.png`; all no border */
  variant?: 'default' | 'plate' | 'cancel' | 'immediate'
  backgroundColor?: string
  borderColor?: string
  textColor?: string
  style?: CSSProperties
  /** Override default plate background image URL (e.g. blacksmith `craft_btn.webp`) */
  plateBgSrc?: string
  /** Override default cancel background image URL */
  cancelBgSrc?: string
  /** Override default immediate background image URL */
  immediateBgSrc?: string
  /** Pins the resource-spend fly-effect destination to this button (forwarded as data-spend-origin
   *  onto the real <button>, so the consumption effect lands here — e.g. "Immediate", "Start"). */
  spendOrigin?: string
  /** data-ftue-anchor forwarded onto the real <button> so guidance spotlights
   *  can target it (same pass-through idea as spendOrigin). */
  ftueAnchor?: string
}

const lazyPlateWrapClass =
  'pointer-events-none absolute inset-0 z-0 block h-full w-full overflow-hidden rounded-[inherit]'

const LazyPlateBg = ({
  src,
  showDisabledOverlay,
}: {
  src: string
  showDisabledOverlay: boolean
}) => (
  <>
    <LazyLoadImage
      src={src}
      alt=""
      effect="blur"
      draggable={false}
      wrapperClassName={lazyPlateWrapClass}
      className="h-full w-full object-fill object-center select-none"
    />
    {showDisabledOverlay ? (
      <span className="pointer-events-none absolute inset-0 z-[1] rounded-[inherit] bg-black/40" aria-hidden />
    ) : null}
  </>
)

export const GameButton: React.FC<GameButtonProps> = ({
  children,
  onClick,
  disabled = false,
  className = '',
  size = 'default',
  variant = 'default',
  backgroundColor = 'rgba(234, 179, 8, 0.3)',
  borderColor = 'rgba(234, 179, 8, 0.8)',
  textColor,
  style,
  plateBgSrc,
  cancelBgSrc,
  immediateBgSrc,
  spendOrigin,
  ftueAnchor,
}) => {
  const getSizeClasses = () => {
    if (size === 'small') {
      if (variant === 'plate' || variant === 'immediate') {
        return 'px-2 py-1 sm:py-2 text-xs sm:text-sm'
      }
      return 'px-2 py-1 sm:py-1.5 text-xs sm:text-sm'
    }
    if (variant === 'plate' || variant === 'immediate') {
      return 'px-4 sm:px-12 py-2 sm:py-3 text-sm sm:text-base'
    }
    return 'px-4 sm:px-6 py-2 sm:py-2.5 text-sm sm:text-base'
  }

  /**
   * Fixed width so START and HH:MM:SS stay the same size (className e.g. w-full overrides).
   * tabular-nums keeps digit columns stable inside the box.
   */
  const plateFixedLayoutClass =
    size === 'small'
      ? 'w-[13rem] max-w-full box-border sm:w-[14rem] inline-flex items-center justify-center shrink-0 tabular-nums text-center'
      : 'w-[16rem] max-w-full box-border sm:w-[17rem] inline-flex items-center justify-center shrink-0 tabular-nums text-center'

  /** Wider than plate when needed so "IMMEDIATE (NNN.NN ECS)" stays on one line. */
  const immediateLayoutClass =
    size === 'small'
      ? 'min-w-[12rem] w-max max-w-[min(100%,95vw)] box-border sm:min-w-[13rem] inline-flex items-center justify-center shrink-0 tabular-nums text-center whitespace-nowrap'
      : 'min-w-[14rem] w-max max-w-[min(100%,95vw)] box-border sm:min-w-[16rem] inline-flex items-center justify-center shrink-0 tabular-nums text-center whitespace-nowrap'

  const getBackgroundColor = () => {
    if (disabled) {
      return 'rgba(100, 100, 100, 0.3)'
    }
    return backgroundColor
  }

  const getBorderColor = () => {
    if (disabled) {
      return 'rgba(150, 150, 150, 0.8)'
    }
    return borderColor
  }

  if (variant === 'plate') {
    return (
      <button
        data-spend-origin={spendOrigin}
        data-ftue-anchor={ftueAnchor}
        onClick={onClick}
        disabled={disabled}
        className={`relative overflow-hidden ${getSizeClasses()} ${plateFixedLayoutClass} rounded-md border-0 transition-opacity disabled:cursor-not-allowed disabled:opacity-60 uppercase ${textColor ? '' : 'text-white'} ${className}`}
        style={{
          fontFamily: 'Tilt Warp',
          textTransform: 'uppercase',
          border: 'none',
          opacity: disabled ? 0.7 : 1,
          ...(textColor ? { color: textColor } : {}),
          ...style,
        }}
      >
        <LazyPlateBg src={plateBgSrc ?? ACTION_PLATE_BUTTON_BG} showDisabledOverlay={false} />
        <span className="relative z-[2] flex h-full w-full items-center justify-center">{children}</span>
      </button>
    )
  }

  if (variant === 'immediate') {
    return (
      <button
        data-spend-origin={spendOrigin}
        data-ftue-anchor={ftueAnchor}
        onClick={onClick}
        disabled={disabled}
        className={`relative overflow-hidden ${getSizeClasses()} ${immediateLayoutClass} rounded-md border-0 transition-opacity disabled:cursor-not-allowed disabled:opacity-60 uppercase ${textColor ? '' : 'text-white'} ${className}`}
        style={{
          fontFamily: 'Tilt Warp',
          textTransform: 'uppercase',
          border: 'none',
          whiteSpace: 'nowrap',
          ...(textColor ? { color: textColor } : {}),
          ...style,
        }}
      >
        <LazyPlateBg src={immediateBgSrc ?? IMMEDIATE_BUTTON_BG} showDisabledOverlay={disabled} />
        <span className="relative z-[2] flex h-full w-full items-center justify-center whitespace-nowrap">
          {children}
        </span>
      </button>
    )
  }

  if (variant === 'cancel') {
    return (
      <button
        data-spend-origin={spendOrigin}
        data-ftue-anchor={ftueAnchor}
        onClick={onClick}
        disabled={disabled}
        className={`relative overflow-hidden ${getSizeClasses()} rounded-md border-0 transition-opacity hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60 uppercase ${textColor ? '' : 'text-white'} ${className}`}
        style={{
          fontFamily: 'Tilt Warp',
          textTransform: 'uppercase',
          border: 'none',
          ...(textColor ? { color: textColor } : {}),
          ...style,
        }}
      >
        <LazyPlateBg src={cancelBgSrc ?? CANCEL_BUTTON_BG} showDisabledOverlay={disabled} />
        <span className="relative z-[2] flex h-full w-full items-center justify-center">{children}</span>
      </button>
    )
  }

  return (
    <button
      data-spend-origin={spendOrigin}
      onClick={onClick}
      disabled={disabled}
      className={`${getSizeClasses()} rounded transition-opacity disabled:opacity-50 disabled:cursor-not-allowed uppercase ${textColor ? '' : 'text-white'} ${className}`}
      style={{
        fontFamily: 'Tilt Warp',
        background: getBackgroundColor(),
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        border: `2px solid ${getBorderColor()}`,
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
        textTransform: 'uppercase',
        ...(textColor ? { color: textColor } : {}),
        ...style,
      }}
    >
      {children}
    </button>
  )
}

export default GameButton
